import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  Add as AddIcon,
  RoomPreferencesOutlined as RoomPreferencesOutlinedIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { customerService } from '../../services/api';

const BusinessManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await customerService.getBusinesses();
      setBusinesses(response.data.results);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBusiness = () => {
    navigate('/business-management/new');
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: 'display_name',
      headerName: t('businessManagement.name'),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.3 }}>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
            UUID: {params.row.uuid.substring(0, 8)}...
          </Typography>
        </Box>
      ),
    },
    {
      field: 'whatsapp_bot_configured',
      headerName: 'WhatsApp Bot',
      flex: 1,
      minWidth: 150,
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Configurado' : 'Não configurado'}
          size="small"
          color={params.value ? 'success' : 'default'}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 100,
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Ativo' : 'Inativo'}
          size="small"
          color={params.value ? 'primary' : 'default'}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('businessManagement.actions'),
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => navigate(`/business-management/edit/${params.row.uuid}`)}
          sx={{ color: 'text.secondary' }}
          title={t('businessManagement.edit')}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RoomPreferencesOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography variant="h5" component="h1">
              {t('businessManagement.title')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBusiness}
            sx={{ textTransform: 'none' }}
          >
            {t('businessManagement.addBusiness')}
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={businesses}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.uuid}
            autoHeight
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            filterMode="client"
            disableColumnMenu={false}
            rowHeight={60}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                fontSize: '0.875rem',
                fontWeight: 600,
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: 1,
                borderColor: 'divider',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
              },
            }}
            localeText={{
              noRowsLabel: t('businessManagement.noBusinesses'),
              columnMenuLabel: t('businessManagement.dataGrid.columnMenuLabel'),
              columnMenuShowColumns: t('businessManagement.dataGrid.columnMenuShowColumns'),
              columnMenuManageColumns: t('businessManagement.dataGrid.columnMenuManageColumns'),
              columnMenuFilter: t('businessManagement.dataGrid.columnMenuFilter'),
              columnMenuHideColumn: t('businessManagement.dataGrid.columnMenuHideColumn'),
              columnMenuUnsort: t('businessManagement.dataGrid.columnMenuUnsort'),
              columnMenuSortAsc: t('businessManagement.dataGrid.columnMenuSortAsc'),
              columnMenuSortDesc: t('businessManagement.dataGrid.columnMenuSortDesc'),
              filterPanelColumns: t('businessManagement.dataGrid.filterPanelColumns'),
              filterPanelOperator: t('businessManagement.dataGrid.filterPanelOperator'),
              filterOperatorContains: t('businessManagement.dataGrid.filterOperatorContains'),
              filterOperatorEquals: t('businessManagement.dataGrid.filterOperatorEquals'),
              filterOperatorStartsWith: t('businessManagement.dataGrid.filterOperatorStartsWith'),
              filterOperatorEndsWith: t('businessManagement.dataGrid.filterOperatorEndsWith'),
              filterOperatorIsEmpty: t('businessManagement.dataGrid.filterOperatorIsEmpty'),
              filterOperatorIsNotEmpty: t('businessManagement.dataGrid.filterOperatorIsNotEmpty'),
              filterOperatorIsAnyOf: t('businessManagement.dataGrid.filterOperatorIsAnyOf'),
              toolbarFilters: t('businessManagement.dataGrid.toolbarFilters'),
              toolbarFiltersLabel: t('businessManagement.dataGrid.toolbarFiltersLabel'),
            }}
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default BusinessManagement;
